import type { ReactNode } from 'react'
import { Pressable, StyleSheet } from 'react-native'

import {
  FigmaCheckIcon,
  FigmaChevronRightIcon,
} from '../icons/FigmaSystemIcons'
import { Box, Text, useTheme } from '../theme/restyle'
import {
  ASSET_GRID_AVATAR_SIZE,
  ASSET_GRID_COLUMNS,
  ASSET_GRID_GAP,
  ASSET_GRID_LABEL_GAP,
  chunk,
} from '../utils/assetGrid'

export interface AssetGridItemProps {
  /** A token symbol, or a chain id. */
  id: string
  /** Size it with `ASSET_GRID_AVATAR_SIZE`. */
  icon: ReactNode
  label: string
  selected: boolean
  /** Offered but not pickable, e.g. a chain that doesn't carry the picked token. */
  unsupported?: boolean
  onPress: () => void
  /** Defaults to `label`. */
  accessibilityLabel?: string
}

export interface AssetGridOverflow {
  /** Reads as a count, e.g. `+7 tokens`. */
  label: string
  onPress: () => void
  accessibilityLabel: string
}

export interface AssetGridProps {
  /** Section header, e.g. "Select token". */
  title: string
  items: AssetGridItemProps[]
  /** The `+N tokens ›` row. Omit when nothing is hidden. */
  overflow?: AssetGridOverflow
}

/**
 * A titled grid of asset cells, five to a row, over an optional row leading to the
 * ones that didn't fit.
 *
 * Chunked into explicit rows of flexed columns, not wrapped on percentage widths:
 * Android rounds a percentage up to the nearest physical pixel, so five 20% cells
 * overflow their row and the fifth wraps.
 */
export function AssetGrid({ title, items, overflow }: AssetGridProps) {
  const theme = useTheme()

  return (
    <Box style={styles.section}>
      <Text variant="bodyMedium" numberOfLines={1} style={styles.title}>
        {title}
      </Text>
      <Box style={styles.grid}>
        {/* Rows are positional, so their index is their identity: keying on a
            cell remounts all five when the list reflows. */}
        {chunk(items, ASSET_GRID_COLUMNS).map((row, rowIndex) => (
          <Box key={rowIndex} flexDirection="row" style={styles.row}>
            {row.map((item) => (
              <AssetGridItem key={item.id} {...item} />
            ))}
            {/* A short last row still has to sit on the column rhythm. */}
            {Array.from(
              { length: ASSET_GRID_COLUMNS - row.length },
              (_, slot) => (
                <Box key={`filler-${slot}`} style={styles.filler} />
              ),
            )}
          </Box>
        ))}
      </Box>
      {overflow ? (
        <Pressable
          onPress={overflow.onPress}
          accessibilityRole="button"
          accessibilityLabel={overflow.accessibilityLabel}
        >
          <Box flexDirection="row" alignItems="center" style={styles.overflow}>
            {/* Without a flexed box the label can't shrink, so a long one pushes
                the chevron past the row's edge instead of ellipsizing. */}
            <Box flex={1}>
              <Text
                variant="bodyMedium"
                numberOfLines={1}
                style={{ color: theme.content.elements.secondary }}
              >
                {overflow.label}
              </Text>
            </Box>
            <FigmaChevronRightIcon
              size={20}
              color={theme.content.elements.secondary}
            />
          </Box>
        </Pressable>
      ) : null}
    </Box>
  )
}

/** One cell of the grid (Figma `icon/crypto-token` + its label). */
export function AssetGridItem({
  icon,
  label,
  selected,
  unsupported = false,
  onPress,
  accessibilityLabel,
}: AssetGridItemProps) {
  const theme = useTheme()
  // Marking a struck-through cell as chosen would contradict itself in the frame
  // before the caller moves the pick off it.
  const isMarked = selected && !unsupported

  return (
    <Pressable
      onPress={onPress}
      disabled={unsupported}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityState={{ selected: isMarked, disabled: unsupported }}
      style={styles.cell}
    >
      <Box alignItems="center" style={styles.stack}>
        <Box style={styles.avatar}>
          <Box
            style={
              unsupported
                ? { opacity: theme.opacity.disabled / 100 }
                : undefined
            }
          >
            {icon}
          </Box>
          {isMarked ? (
            <>
              {/* Two rings over the logo rather than a smaller logo inside a
                  border: the design crops the avatar, it doesn't scale it. */}
              <Box
                pointerEvents="none"
                style={[
                  styles.ringGap,
                  { borderColor: theme.background.modal },
                ]}
              />
              <Box
                pointerEvents="none"
                style={[
                  styles.ring,
                  { borderColor: theme.content.elements.primary },
                ]}
              />
              <Box
                alignItems="center"
                justifyContent="center"
                style={[
                  styles.badge,
                  {
                    backgroundColor: theme.content.elements.primary,
                    borderColor: theme.background.modal,
                  },
                ]}
              >
                <FigmaCheckIcon
                  size={12}
                  color={theme.content.button.primary.default}
                />
              </Box>
            </>
          ) : null}
        </Box>
        <Text
          variant="detail"
          weight="medium"
          numberOfLines={1}
          style={[
            styles.label,
            unsupported
              ? {
                  color: theme.content.elements.secondary,
                  textDecorationLine: 'line-through',
                }
              : { color: theme.content.elements.primary },
          ]}
        >
          {label}
        </Text>
      </Box>
    </Pressable>
  )
}

const BADGE_SIZE = 24

const styles = StyleSheet.create({
  section: { gap: 4 },
  title: { paddingBottom: 12 },
  grid: { gap: ASSET_GRID_GAP },
  row: { gap: ASSET_GRID_GAP },
  filler: { flex: 1 },
  overflow: { gap: 6, paddingVertical: 13 },
  // Cells are sized by the row they sit in, not by a width of their own.
  cell: { flex: 1 },
  stack: { gap: 6 },
  avatar: { width: ASSET_GRID_AVATAR_SIZE, height: ASSET_GRID_AVATAR_SIZE },
  ringGap: {
    ...StyleSheet.absoluteFill,
    borderRadius: 999,
    borderWidth: 4,
  },
  ring: { ...StyleSheet.absoluteFill, borderRadius: 999, borderWidth: 2 },
  badge: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    width: BADGE_SIZE,
    height: BADGE_SIZE,
    borderRadius: 999,
    borderWidth: 2,
  },
  // A chain name runs wider than the avatar, so it borrows from the gutter either
  // side rather than ellipsizing at the cell's edge — short of the share that
  // would let it touch the next label.
  label: {
    textAlign: 'center',
    alignSelf: 'stretch',
    marginHorizontal: -(ASSET_GRID_GAP - ASSET_GRID_LABEL_GAP) / 2,
  },
})
